import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Anchor, Compass, Ship, Wind, Map, ChevronRight, Shield, Navigation } from 'lucide-react';

const MaritimeAcademy: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Royal Maritime Academy";
    
    return (
        <div className="min-h-screen bg-slate-50 font-serif text-slate-900 selection:bg-[#0a2540] selection:text-white">
            {/* Top Bar - Official Status */}
            <div className="bg-[#0a2540] text-slate-300 py-3 px-8 text-xs font-sans font-medium tracking-wider flex justify-between items-center">
                <span>ISO 9001:2015 Certified Institution</span>
                <div className="flex gap-6">
                    <a href="#" className="hover:text-white transition-colors">Academy Portal</a>
                    <a href="#" className="hover:text-white transition-colors">Safety Protocols</a>
                    <a href="#" className="hover:text-white transition-colors">Alumni Network</a>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-8 h-24 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#0a2540] text-white flex items-center justify-center rounded-sm shadow-md">
                            <Anchor size={24} />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-bold text-xl uppercase tracking-widest text-[#0a2540] leading-none">{schoolName}</span>
                            <span className="text-[10px] font-sans font-bold tracking-[0.3em] text-[#c5a059] uppercase mt-1">Harness the Sea</span>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-10 font-sans text-xs font-bold text-slate-600 uppercase tracking-widest">
                        <a href="#" className="hover:text-[#0a2540] transition-colors">Cadet Life</a>
                        <a href="#" className="hover:text-[#0a2540] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#0a2540] transition-colors">Training Fleet</a>
                        <a href="#" className="hover:text-[#0a2540] transition-colors">Admissions</a>
                    </div>

                    <Button className="font-sans bg-[#c5a059] hover:bg-[#b08d4b] text-[#0a2540] font-bold uppercase tracking-widest text-xs h-10 px-6 rounded-sm">
                        Apply Now
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative h-[85vh] flex items-center justify-center overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://images.unsplash.com/photo-1500930267207-e85d886e00da?q=80&w=2070&auto=format&fit=crop" 
                        alt="Ocean Waves" 
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#0a2540]/60 mix-blend-multiply"></div>
                </div>

                <div className="relative z-10 max-w-5xl mx-auto text-center text-white px-6">
                    <div className="inline-block border border-[#c5a059] px-4 py-2 mb-8 bg-[#0a2540]/50 backdrop-blur-sm">
                        <span className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] flex items-center gap-2">
                             <Compass size={14} /> Est. 1824
                        </span>
                    </div>
                    
                    <h1 className="text-6xl md:text-8xl font-bold mb-8 leading-none tracking-tight">
                        Command <br/>
                        <span className="text-[#bfd7ed] font-light italic">The Horizon.</span>
                    </h1>
                    
                    <p className="font-sans text-lg md:text-xl text-slate-200 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
                        Developing the next generation of merchant marine officers and naval engineers through rigorous academic and practical training.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-6">
                        <Button className="font-sans bg-white text-[#0a2540] hover:bg-slate-100 font-bold uppercase tracking-widest text-sm h-14 px-10 rounded-sm">
                            Explore Degrees
                        </Button>
                        <Button variant="outline" className="font-sans border-white text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm h-14 px-10 rounded-sm bg-transparent">
                            Virtual Tour
                        </Button>
                    </div>
                </div>
                
                {/* Scroll Indicator */}
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 animate-bounce">
                    <span className="font-sans text-[10px] uppercase tracking-widest">Scroll</span>
                    <div className="w-px h-12 bg-white/30"></div>
                </div>
            </header>

            {/* Statistics Strip */}
            <div className="bg-[#0a2540] py-12 px-6 border-b border-[#1e3a5f]">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-[#1e3a5f]">
                    {[
                        { label: "Placement Rate", value: "100%" },
                        { label: "Training Vessels", value: "12" },
                        { label: "Years of Heritage", value: "200+" },
                        { label: "Cadet Corps", value: "1,500" },
                    ].map((stat, i) => (
                        <div key={i} className="text-center px-4">
                            <div className="text-4xl text-white font-bold mb-1">{stat.value}</div>
                            <div className="font-sans text-[10px] uppercase tracking-widest text-[#c5a059]">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Departments */}
            <section className="py-24 px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-[#0a2540] mb-4">Academic Divisions</h2>
                        <div className="w-24 h-1 bg-[#c5a059] mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { icon: Navigation, title: "Marine Transportation", desc: "Navigation, seamanship, and ship handling certification." },
                            { icon: Ship, title: "Marine Engineering", desc: "Propulsion systems, electrical power, and mechanical maintenance." },
                            { icon: Shield, title: "Maritime Security", desc: "International law, port security, and logistics management." }
                        ].map((item, i) => (
                            <div key={i} className="group p-8 border border-slate-200 hover:border-[#0a2540] transition-colors bg-slate-50 hover:bg-white">
                                <item.icon size={48} className="text-[#0a2540] mb-6 stroke-1" />
                                <h3 className="text-2xl font-bold text-[#0a2540] mb-4">{item.title}</h3>
                                <p className="font-sans text-slate-500 leading-relaxed mb-8">{item.desc}</p>
                                <a href="#" className="font-sans text-[#c5a059] font-bold text-xs uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all">
                                    View Curriculum <ChevronRight size={14} />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1e2022] text-white py-20 px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1 md:col-span-2">
                         <div className="flex items-center gap-3 mb-6">
                            <Anchor size={20} className="text-[#c5a059]" />
                            <span className="font-bold text-lg uppercase tracking-widest">{schoolName}</span>
                         </div>
                         <p className="font-sans text-slate-400 text-sm max-w-sm leading-relaxed">
                             A premier federal service academy dedicated to educating and graduating leaders of exemplary character.
                         </p>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 text-center font-sans text-xs text-slate-500 uppercase tracking-widest">
                    &copy; 2026 {schoolName}. Semper Fidelis.
                </div>
            </footer>
        </div>
    );
};

export default MaritimeAcademy;
